import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import useDropdownDelay, { DROPDOWN_CLOSE_DELAY_MS } from '../hooks/use-dropdown-delay';
import useHydrationSafe from '../hooks/use-hydration-safe';
import { useMicrosite } from './franchise/microsite-context';
import transformDynamic from '../templates/franchise/utils/transform-dynamic';
import type { SiteNavigationSlices } from '../components/header/navigation/site-navigation-types';

/** Re-dispatch the sidebar mega-menu script's hover path so the tab under the cursor opens after leaving the orange strip. */
export const mirrorPrimaryMegaMenuHover = (entered: Node): void => {
    if (!(entered instanceof Element)) return;
    const item = entered.closest('[data-sidebar-primary-nav] [data-menu="item"]');
    const hitbox = item?.querySelector('[data-menu="trigger-host"]')
        ?? item?.querySelector('[data-menu="trigger"]');
    if (hitbox instanceof HTMLElement) {
        hitbox.dispatchEvent(new MouseEvent('mouseover', { bubbles: false, cancelable: true, view: window }));
    }
};

const clickedLink = (event: MouseEvent): HTMLAnchorElement | null => {
    const link = (event.target as Element | null)?.closest?.('a[href]');
    // SVG anchors share the selector but none of the HTML anchor API.
    return link instanceof HTMLAnchorElement ? link : null;
};

/** Clicks the browser must keep: new tab/window, download, or a modified/secondary click. */
const isNativeClick = (link: HTMLAnchorElement, event: MouseEvent): boolean => event.defaultPrevented
    || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0
    || (!!link.target && link.target.toLowerCase() !== '_self')
    || link.hasAttribute('download');

/** True only when the link resolves to exactly the page already shown - no query or hash change. */
const isCurrentPage = (link: HTMLAnchorElement, pathname: string, search: string): boolean => {
    let url: URL;
    try {
        url = new URL(link.getAttribute('href') ?? '', window.location.href);
    } catch {
        return false;
    }
    const normalize = (value: string): string => value.replace(/\/+$/, '') || '/';
    return url.origin === window.location.origin
        && normalize(url.pathname) === normalize(pathname)
        && url.search === (search || '')
        && !url.hash;
};

interface NavigationContextType {
    registerMobileNavClose: (closeFn: () => void) => void;
    /** Corporate orange strip: closes React dropdown from `SidebarTopNav`. */
    registerSecondaryTopNavClose: (closeFn: () => void) => void;
    closeAllNavigation: () => void;
    /** Variant header: close mega menu (primary) without delay — synthetic leave on nav root. */
    dismissPrimaryMegaMenu: () => void;
    /** Variant header: close corporate strip dropdown (secondary) without delay. */
    dismissSecondaryTopNavDropdown: () => void;
    isHydrated: boolean;
    /** National or microsite menu (microsite strings run through transformDynamic for locationName). */
    siteNavigation: SiteNavigationSlices | null;
    dropdownDelay: {
        handleMouseEnter: () => void;
        handleMouseLeave: () => void;
        cancelDelay: () => void;
        hideImmediately: () => void;
    };
}

const NavigationContext = React.createContext<NavigationContextType>({
    registerMobileNavClose: () => {},
    registerSecondaryTopNavClose: () => {},
    closeAllNavigation: () => {},
    dismissPrimaryMegaMenu: () => {},
    dismissSecondaryTopNavDropdown: () => {},
    isHydrated: false,
    siteNavigation: null,
    dropdownDelay: {
        handleMouseEnter: () => {},
        handleMouseLeave: () => {},
        cancelDelay: () => {},
        hideImmediately: () => {},
    },
});

export const useNavigationContext = (): NavigationContextType => {
    const context = React.useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigationContext must be used within a NavigationProvider');
    }
    return context;
};

export const NavigationProvider = ({ children }: React.PropsWithChildren<unknown>): JSX.Element => {
    const location = useLocation();
    const mobileNavCloseRef = React.useRef<(() => void) | null>(null);
    const secondaryTopNavCloseRef = React.useRef<(() => void) | null>(null);

    const { isMicrosite, newMenu, franchise } = useMicrosite();

    const {
        csSiteNavigation: nationalMenu,
    } = useStaticQuery<Queries.SiteNavigationHeaderStaticQuery>(
        graphql`
            query SiteNavigationHeaderStatic {
              csSiteNavigation(title: { eq: "National Site Navigation" }) {
                id
                title
                primary {
                  title
                  link
                  itemInfo {
                    title
                    content
                    contentAsPlainText
                    image {
                      url
                      title
                      filename
                      description
                      contentStackAsset(width: 720)
                    }
                  }
                  items {
                    title
                    link
                    icon {
                      url
                      title
                    }
                    itemInfo {
                      title
                      content
                      contentAsPlainText
                      image {
                        url
                        title
                        filename
                        description
                        contentStackAsset(width: 720)
                      }
                    }
                    items {
                      link
                      title
                      icon {
                        url
                        title
                      }
                    }
                  }
                }
                secondary {
                  title
                  link
                  items {
                    title
                    link
                    items {
                      title
                      link
                    }
                  }
                }
              }
            }
        `,
    );

    // franchiseCount is substituted at Gatsby build (see get-config injectVariables + field-processors).
    // Per-franchise locationName must be resolved at runtime (same transformDynamic pattern as microsite-page).
    const reparsedMicrositeMenu = React.useMemo((): SiteNavigationSlices | null => {
        if (!isMicrosite || !newMenu || !franchise) {
            return null;
        }
        return transformDynamic(newMenu, {
            locationName: franchise.name ?? '',
        }) as SiteNavigationSlices;
    }, [isMicrosite, newMenu, franchise]);

    const siteNavigation = React.useMemo((): SiteNavigationSlices | null => {
        if (isMicrosite) {
            return reparsedMicrositeMenu ?? newMenu ?? null;
        }
        return nationalMenu;
    }, [isMicrosite, reparsedMicrositeMenu, newMenu, nationalMenu]);

    // Track when hydration is complete to prevent hydration errors
    const isHydrated = useHydrationSafe();

    const registerMobileNavClose = React.useCallback((closeFn: () => void) => {
        mobileNavCloseRef.current = closeFn;
    }, []);

    const registerSecondaryTopNavClose = React.useCallback((closeFn: () => void) => {
        secondaryTopNavCloseRef.current = closeFn;
    }, []);

    const closeAllNavigation = React.useCallback(() => {
        // Close mobile navigation
        if (mobileNavCloseRef.current) {
            mobileNavCloseRef.current();
        }

        if (secondaryTopNavCloseRef.current) {
            secondaryTopNavCloseRef.current();
        }

        // Also trigger the native menu hide functionality
        // The navigation-menu script exposes this globally when mounted
        const allMenuItems = document.querySelectorAll('[data-menu="item"]');
        allMenuItems.forEach((item) => {
            if (item instanceof HTMLElement) {
                const trigger = item.querySelector('[data-menu="trigger"]');
                const content = item.querySelector('[data-menu="content"]');
                if (trigger instanceof HTMLElement && content && trigger.dataset.state === 'open') {
                    // Simulate the hideMenu functionality from the navigation script
                    const mouseLeaveEvent = new Event('mouseleave');
                    const parentNav = item.closest('[data-menu-root-scope-id]');
                    if (parentNav) {
                        parentNav.dispatchEvent(mouseLeaveEvent);
                    }
                }
            }
        });
    }, []);

    // Dropdown delay hook for desktop navigation with 1 second delay
    // Note: onHide uses closeAllNavigation which handles both mobile and desktop nav closing
    const dropdownDelay = useDropdownDelay({
        delay: DROPDOWN_CLOSE_DELAY_MS,
        onHide: closeAllNavigation,
    });

    const dismissSecondaryTopNavDropdown = React.useCallback((): void => {
        dropdownDelay.cancelDelay();
        if (secondaryTopNavCloseRef.current) {
            secondaryTopNavCloseRef.current();
        }
    }, [dropdownDelay]);

    const dismissPrimaryMegaMenu = React.useCallback((): void => {
        const root = document.querySelector('[data-sidebar-primary-nav][data-menu-root-scope-id]');
        if (root) {
            root.dispatchEvent(new Event('mouseleave'));
        }
    }, []);

    // Suppressing a link that points at the page already shown has to happen during
    // capture: Gatsby's Link bails on `event.defaultPrevented`, and its React handler
    // runs before any bubble-phase listener on `document`. Nothing closes here -
    // unmounting during capture removes the link before React ever sees the click.
    React.useEffect(() => {
        const suppressCurrentPageClick = (event: MouseEvent): void => {
            const link = clickedLink(event);
            if (!link || isNativeClick(link, event)) return;
            if (isCurrentPage(link, location.pathname, location.search)) {
                event.preventDefault();
            }
        };

        document.addEventListener('click', suppressCurrentPageClick, true);
        return () => document.removeEventListener('click', suppressCurrentPageClick, true);
    }, [location.pathname, location.search]);

    // Global click listener to handle navigation closing. Bubble phase, so the Link has
    // already navigated (or the capture listener above has suppressed a same-page click).
    React.useEffect(() => {
        const handleLinkClick = (event: MouseEvent): void => {
            const link = clickedLink(event);
            if (!link) return;

            // Close navigation if:
            // 1. Clicking a link to the current page (already suppressed during capture), OR
            // 2. Clicking any link in the top nav dropdown (close immediately), OR
            // 3. Clicking any link in the mobile nav (close immediately)
            if (isCurrentPage(link, location.pathname, location.search)) {
                closeAllNavigation();
            } else if (link.closest('[data-top-nav-dropdown]')) {
                // Close dropdown immediately for any link click (no delay)
                // Cancel any pending delays first
                dropdownDelay.cancelDelay();
                // Then close the dropdown
                if (secondaryTopNavCloseRef.current) {
                    secondaryTopNavCloseRef.current();
                }
            } else if (link.closest('#mobile-nav-drawer')) {
                // Close mobile nav immediately for any link click (no delay)
                if (mobileNavCloseRef.current) {
                    mobileNavCloseRef.current();
                }
            }
        };

        document.addEventListener('click', handleLinkClick);
        return () => document.removeEventListener('click', handleLinkClick);
    }, [location.pathname, location.search, closeAllNavigation, dropdownDelay]);

    const value = React.useMemo(() => ({
        registerMobileNavClose,
        registerSecondaryTopNavClose,
        closeAllNavigation,
        dismissPrimaryMegaMenu,
        dismissSecondaryTopNavDropdown,
        isHydrated,
        siteNavigation,
        dropdownDelay,
    }), [
        registerMobileNavClose,
        registerSecondaryTopNavClose,
        closeAllNavigation,
        dismissPrimaryMegaMenu,
        dismissSecondaryTopNavDropdown,
        isHydrated,
        siteNavigation,
        dropdownDelay,
    ]);

    return (
        <NavigationContext.Provider value={value}>
            {children}
        </NavigationContext.Provider>
    );
};

export default NavigationContext;
